import { useState, useEffect, useRef, memo, useMemo, useCallback } from "react";
import { usePlaceholder } from '../../../hooks/usePlaceholder';
import { ChevronsUpDown, Check } from "lucide-react";
import {
  getPaises,
  getAduanas,
  getModosTransporteFronteraPredefinidos,
  getMediosTransporteFrontera,
  getExportacionesTipos,
  getTipoUbicacionOptions,
  getDivisasISO,
  getCondicionesEntrega,
  getClavesDeclarante,
  getDeclaraciones1,
  getDeclaraciones2,
  getCodigosUN,
} from '../../../utils/modalDataUtils';

import { OptimizedInput } from '../../common/OptimizedInput';
import { getCurrentTimestamp } from '../../../utils/declarationSummaryUtils';
import { useFieldHandlerFactory } from '../../../utils/fieldHandlers';
import { UniversalSelectModal } from '../../common/UniversalSelectModal';
import { getModalConfig } from '../../../utils/modalConfigUtils';

// Reusable Binary Toggle Component (0/1 selector)
const BinaryToggle = ({ id, value, onChange, label0, label1 }) => {
  // Normalize value: handle undefined, null, empty string, and string numbers
  const normalizedValue = value === undefined || value === null || value === "" 
    ? null 
    : (typeof value === 'string' ? Number(value) : value);
  
  // Use local state for immediate UI feedback (optimistic update) This ensures the check mark appears immediately, even before Redux updates
  const [localValue, setLocalValue] = useState(normalizedValue);
  
  // Sync local state when prop value changes (from Redux update)
  useEffect(() => {
    setLocalValue(normalizedValue);
  }, [normalizedValue]);
  
  const isSelected0 = localValue === 0;
  const isSelected1 = localValue === 1;
  const isUnselected = localValue === null;

  const handleClick = (newValue, e) => {
    // Prevent any default behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // OPTIMISTIC UPDATE: Update local state immediately for instant UI feedback
    const numericValue = typeof newValue === 'string' ? Number(newValue) : newValue;
    setLocalValue(numericValue);
    
    if (onChange) {
      // Create a synthetic event-like object for consistency
      const syntheticEvent = {
        target: { value: numericValue },
        preventDefault: () => {},
        stopPropagation: () => {}
      };
      
      onChange(syntheticEvent);
    }
  };

  return (
    <div id={id} className="flex gap-1 mt-1" style={{ pointerEvents: 'auto', zIndex: 1 }}>
      <button
        id={id ? `${id}_0` : undefined}
        type="button"
        onClick={(e) => handleClick(0, e)}
        onMouseDown={(e) => e.stopPropagation()}
        className={`flex items-center justify-center gap-1.5 py-1 rounded-full transition-colors cursor-pointer ${
          isSelected0
            ? 'bg-secondary text-default font-semibold px-3'
            : isUnselected
            ? 'bg-gray-200 text-gray-600 px-4 hover:bg-gray-300'
            : 'bg-gray-200 text-gray-400 px-4 hover:bg-gray-300'
        }`}
        style={{ pointerEvents: 'auto', zIndex: 2 }}
      >
        {isSelected0 && <Check className="w-3 h-3" />}
        <span className="text-xs">{label0 || '0'}</span>
      </button>
      <button
        id={id ? `${id}_1` : undefined}
        type="button"
        onClick={(e) => handleClick(1, e)}
        onMouseDown={(e) => e.stopPropagation()}
        className={`flex items-center justify-center gap-2 py-1 rounded-full transition-colors cursor-pointer ${
          isSelected1
            ? 'bg-secondary text-default font-semibold px-3'
            : isUnselected
            ? 'bg-gray-200 text-gray-600 px-4 hover:bg-gray-300'
            : 'bg-gray-200 text-gray-400 px-4 hover:bg-gray-300'
        }`}
        style={{ pointerEvents: 'auto', zIndex: 2 }}
      >
        {isSelected1 && <Check className="w-3 h-3" />}
        <span className="text-xs">{label1 || '1'}</span>
      </button>
    </div>
  );
};

const DeclarationInformationComponent = ({
  handleChange,
  declarationIndex,
  isSpanish,
  declaration,
  setEditableData,
  setIsDirty,
}) => {
  
  // Memoize the onChange wrapper to prevent recreating handlers on every render This ensures getFieldHandler returns stable handlers
  const onChangeWrapper = useCallback((field, value) => {
    handleChange(declarationIndex, field, value);
  }, [handleChange, declarationIndex]);
  
  // Binary fields that should always be numbers (0 or 1)
  const binaryFields = new Set([
    'flag_datos_seguridad',
    'es_simplificado',
    'es_recapitulativo',
    'completo',
    'es_plantilla',
    'autorizacion_por_operacion'
  ]);

  const getFieldHandler = useFieldHandlerFactory(
    onChangeWrapper,
    {
      transformValue: (e, field) => {
        // Handle binary fields - ensure they're always numbers (0 or 1)
        if (binaryFields.has(field)) {
          const value = e.target.value;
          // Convert to number if it's a string, otherwise use as-is
          return typeof value === 'string' ? Number(value) : value;
        }
        if (field === 'estado_pdc') {
          return e.target.value === "" ? 0 : Number(e.target.value);
        }
        return e.target.value;
      }
    }
  );

  // Memoize commonly used field handlers to prevent recreating on every render This improves performance by ensuring stable function references
  const handleFechaRegistro = useMemo(() => getFieldHandler("fecha_registro"), [getFieldHandler]);
  const handleFechaCreacion = useMemo(() => getFieldHandler("fecha_creacion"), [getFieldHandler]);
  const handleFechaOperacion = useMemo(() => getFieldHandler("fecha_operacion"), [getFieldHandler]);
  const handleMrn = useMemo(() => getFieldHandler("mrn"), [getFieldHandler]);
  const handleDeclaracion3 = useMemo(() => getFieldHandler("declaracion_3"), [getFieldHandler]);
  const handleClienteId = useMemo(() => getFieldHandler("cliente_id"), [getFieldHandler]);
  const handleRemitenteId = useMemo(() => getFieldHandler("remitente_id"), [getFieldHandler]);
  const handleModoTransporteInterior = useMemo(() => getFieldHandler("modo_transporte_interior"), [getFieldHandler]);
  const handleIdentidadTransporteFrontera = useMemo(() => getFieldHandler("identidad_transporte_frontera"), [getFieldHandler]);
  const handleEstadoPdc = useMemo(() => getFieldHandler("estado_pdc"), [getFieldHandler]);
  const handleEstadoEdi = useMemo(() => getFieldHandler("estado_edi"), [getFieldHandler]);
  const handleEstadoEdiCanarias = useMemo(() => getFieldHandler("estado_edi_canarias"), [getFieldHandler]);
  const handleNaturalezaTransaccion = useMemo(() => getFieldHandler("naturaleza_transaccion"), [getFieldHandler]);
  const handleObservaciones = useMemo(() => getFieldHandler("observaciones"), [getFieldHandler]);
  const handleFactura = useMemo(() => getFieldHandler("factura"), [getFieldHandler]);
  const handleFechaFactura = useMemo(() => getFieldHandler("fecha_factura"), [getFieldHandler]);
  const handleCambioDivisa = useMemo(() => getFieldHandler("cambio_divisa"), [getFieldHandler]);
  const handleLocalizacion = useMemo(() => getFieldHandler("localizacion"), [getFieldHandler]);
  const handleTipoAutorizacionCd = useMemo(() => getFieldHandler("tipo_autorizacion_cd"), [getFieldHandler]);
  const handleIdentificacionAdicional = useMemo(() => getFieldHandler("identificacion_adicional"), [getFieldHandler]);
  const handleTieneContenedores = useMemo(() => getFieldHandler("tiene_contenedores"), [getFieldHandler]);
  const handleTotalBultos = useMemo(() => getFieldHandler("total_bultos"), [getFieldHandler]);
  const handleEmail = useMemo(() => getFieldHandler("e_mail"), [getFieldHandler]);
  const handleContactoTelefono = useMemo(() => getFieldHandler("contacto_telefono"), [getFieldHandler]);
  const handleCompleto = useMemo(() => getFieldHandler("completo"), [getFieldHandler]);
  const handleEsPlantilla = useMemo(() => getFieldHandler("es_plantilla"), [getFieldHandler]);
  const handleFlagDatosSeguridad = useMemo(() => getFieldHandler("flag_datos_seguridad"), [getFieldHandler]);
  const handleEsSimplificado = useMemo(() => getFieldHandler("es_simplificado"), [getFieldHandler]);
  const handleEsRecapitulativo = useMemo(() => getFieldHandler("es_recapitulativo"), [getFieldHandler]);
  const handleAutorizacionPorOperacion = useMemo(() => getFieldHandler("autorizacion_por_operacion"), [getFieldHandler]);

  // Memoize all placeholder values to prevent unnecessary re-renders
  const mrnPlaceholder = usePlaceholder({ isSpanish, fieldValue: declaration.mrn, type: 'mrn' });
  const example0Placeholder = usePlaceholder({ isSpanish, type: 'example_0' });
  const example0_00Placeholder = usePlaceholder({ isSpanish, type: 'example_0_00' });
  const example512Placeholder = usePlaceholder({ isSpanish, type: 'example_512' });
  const example916769999Placeholder = usePlaceholder({ isSpanish, type: 'example_916769999' });
  const example280112345Placeholder = usePlaceholder({ isSpanish, type: 'example_280112345' });
  const exampleSurreyPlaceholder = usePlaceholder({ isSpanish, type: 'example_surrey' });
  const example1_0Placeholder = usePlaceholder({ isSpanish, type: 'example_1_0' });
  const exampleEmailPlaceholder = usePlaceholder({ isSpanish, type: 'example_email' });
  
  // Conditional placeholders (depend on field value)
  const fechaRegistroPlaceholder = usePlaceholder({ isSpanish, fieldValue: declaration.fecha_registro, type: 'enter_registration_date' });
  const declaracion3Placeholder = usePlaceholder({ isSpanish, fieldValue: declaration.declaracion_3, type: 'enter_declaration_3' });
  const clienteIdPlaceholder = usePlaceholder({ isSpanish, fieldValue: declaration.cliente_id, type: 'enter_client_id' });
  const remitenteIdPlaceholder = usePlaceholder({ isSpanish, fieldValue: declaration.remitente_id, type: 'enter_sender_id' });
  const agenteIdPlaceholder = usePlaceholder({ isSpanish, fieldValue: declaration.agente_id, type: 'enter_agent_id' });
  const modoTransporteInteriorPlaceholder = usePlaceholder({ isSpanish, fieldValue: declaration.modo_transporte_interior, type: 'enter_internal_transport_mode' });
  const totalBultosPlaceholder = usePlaceholder({ isSpanish, fieldValue: declaration.total_bultos, type: 'enter_total_packages' });
  const identidadTransporteFronteraPlaceholder = usePlaceholder({ isSpanish, fieldValue: declaration.identidad_transporte_frontera, type: 'enter_border_transport_identity' });
  const estadoEdiPlaceholder = usePlaceholder({ isSpanish, fieldValue: declaration.estado_edi, type: 'enter_edi_status' });
  const estadoEdiCanariasPlaceholder = usePlaceholder({ isSpanish, fieldValue: declaration.estado_edi_canarias, type: 'enter_canary_islands_edi_status' });
  const naturalezaTransaccionPlaceholder = usePlaceholder({ isSpanish, fieldValue: declaration.naturaleza_transaccion, type: 'enter_transaction_nature' });
  const observacionesPlaceholder = usePlaceholder({ isSpanish, fieldValue: declaration.observaciones, type: 'enter_observations_simple' });
  const facturaPlaceholder = usePlaceholder({ isSpanish, fieldValue: declaration.factura, type: 'enter_invoice' });
  const fechaFacturaPlaceholder = usePlaceholder({ isSpanish, fieldValue: declaration.fecha_factura, type: 'enter_invoice_date' });
  const tipoAutorizacionCdPlaceholder = usePlaceholder({ isSpanish, fieldValue: declaration.tipo_autorizacion_cd, type: 'enter_type' });
  const identificacionAdicionalPlaceholder = usePlaceholder({ isSpanish, fieldValue: declaration.identificacion_adicional, type: 'enter_additional_identification' });
  const tieneContenedoresPlaceholder = usePlaceholder({ isSpanish, fieldValue: declaration.tiene_contenedores, type: 'enter_has_containers' });
  
  // const userData = useSelector((state) => state.user.userData);
  const [isPaisDestinoModalOpen, setPaisDestinoModalOpen] = useState(false);
  const [isPaisExportacionModalOpen, setPaisExportacionModalOpen] =
    useState(false);
  const [
    isPaisTransporteFronteraModalOpen,
    setPaisTransporteFronteraModalOpen,
  ] = useState(false);
  const [isAduanaEntradaModalOpen, setAduanaEntradaModalOpen] = useState(false);
  const [isAduanaSalidaModalOpen, setAduanaSalidaModalOpen] = useState(false);
  const [isAduanaDespachoModalOpen, setAduanaDespachoModalOpen] =
    useState(false);
  const [isModoFronteraModalOpen, setModoFronteraModalOpen] = useState(false);
  const [isMedioFronteraModalOpen, setMedioFronteraModalOpen] = useState(false);
  const [isExportacionesTipoModalOpen, setExportacionesTipoModalOpen] =
    useState(false);
  const [isPaisUbicacionModalOpen, setPaisUbicacionModalOpen] = useState(false);
  const [isTipoUbicacionModalOpen, setTipoUbicacionModalOpen] = useState(false);
  const [isDivisaModalOpen, setDivisaModalOpen] = useState(false);
  const [isDivisaIsoModalOpen, setDivisaIsoModalOpen] = useState(false);
  const [isCondicionesEntregaModalOpen, setCondicionesEntregaModalOpen] =
    useState(false);
  const [isClaveDeclaranteModalOpen, setClaveDeclaranteModalOpen] = useState(false);
  const [isDeclaracion1ModalOpen, setDeclaracion1ModalOpen] = useState(false);
  const [isDeclaracion2ModalOpen, setDeclaracion2ModalOpen] = useState(false);
  const [isCodigoUNModalOpen, setCodigoUNModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("All");

  // Memoize lookup data to avoid recalculating on every render
  const paises = useMemo(() => getPaises(), []);
  const aduanasEntrada = useMemo(() => getAduanas('aduanas-espanolas'), []);
  const aduanasSalida = useMemo(() => getAduanas(['aduanas-comunitarias-espanolas', 'aduanas-comunitarias-no-espanolas']), []);
  const aduanasDespacho = useMemo(() => getAduanas('aduanas-espanolas'), []);
  const modosTransporteFronteraPredefinidos = useMemo(() => getModosTransporteFronteraPredefinidos(), []);
  const mediosTransporteFrontera = useMemo(() => getMediosTransporteFrontera(), []);
  const exportacionesTipos = useMemo(() => getExportacionesTipos(), []);
  const tipoUbicacionOptions = useMemo(() => getTipoUbicacionOptions(), []);
  const divisas = useMemo(() => getDivisasISO(), []);
  const clavesDeclarante = useMemo(() => getClavesDeclarante(), []);
  const declaraciones1 = useMemo(() => getDeclaraciones1(), []);
  const declaraciones2 = useMemo(() => getDeclaraciones2(), []);
  const codigosUN = useMemo(() => getCodigosUN(), []);
  const condicionesEntrega = useMemo(() => getCondicionesEntrega(), []);

  // Memoize modal configs to avoid creating new objects on every render
  const modalConfigNameCode = useMemo(() => getModalConfig('nameCode', isSpanish), [isSpanish]);
  const modalConfigCodeName = useMemo(() => getModalConfig('codeName', isSpanish), [isSpanish]);
  const modalConfigCodeDesc = useMemo(() => getModalConfig('codeDesc', isSpanish), [isSpanish]);
  const modalConfigIsoDesc = useMemo(() => getModalConfig('isoDesc', isSpanish), [isSpanish]);
  const modalConfigNameOnly = useMemo(() => getModalConfig('nameOnly', isSpanish), [isSpanish]);

  // Memoize selected objects to avoid recalculating on every render
  const selectedPaisDestino = useMemo(() => paises.find(
    (p) =>
      p.codigo === declaration.pais_destino ||
      p.nombre === declaration.pais_destino
  ), [paises, declaration.pais_destino]);
  
  const selectedPaisExportacion = useMemo(() => paises.find(
    (p) =>
      p.codigo === declaration.pais_exportacion ||
      p.nombre === declaration.pais_exportacion
  ), [paises, declaration.pais_exportacion]);
  
  const selectedPaisTransporteFrontera = useMemo(() => paises.find(
    (p) =>
      p.codigo === declaration.pais_transporte_frontera ||
      p.nombre === declaration.pais_transporte_frontera
  ), [paises, declaration.pais_transporte_frontera]);
  
  const selectedPaisUbicacion = useMemo(() => paises.find(
    (p) =>
      p.codigo === declaration.pais_ubicacion ||
      p.nombre === declaration.pais_ubicacion
  ), [paises, declaration.pais_ubicacion]);
  
  const selectedAduanaEntrada = useMemo(() => aduanasEntrada.find(
    (a) =>
      a.codigo === declaration.aduana_entrada ||
      a.nombre === declaration.aduana_entrada
  ), [aduanasEntrada, declaration.aduana_entrada]);
  
  const selectedAduanaSalida = useMemo(() => aduanasSalida.find(
    (a) =>
      a.codigo === declaration.aduana_salida ||
      a.nombre === declaration.aduana_salida
  ), [aduanasSalida, declaration.aduana_salida]);
  
  const selectedAduanaDespacho = useMemo(() => aduanasDespacho.find(
    (a) =>
      a.codigo === declaration.aduana_despacho ||
      a.nombre === declaration.aduana_despacho
  ), [aduanasDespacho, declaration.aduana_despacho]);

  const selectedModoFrontera = useMemo(() => modosTransporteFronteraPredefinidos.find(
    (m) =>
      m.codigo === declaration.modo_transporte_frontera ||
      m.nombre === declaration.modo_transporte_frontera
  ), [modosTransporteFronteraPredefinidos, declaration.modo_transporte_frontera]);

  const selectedMedioTransporteFrontera = useMemo(() => mediosTransporteFrontera.find(
    (m) =>
      m.codigo === declaration.medio_transporte_frontera ||
      m.nombre === declaration.medio_transporte_frontera
  ), [mediosTransporteFrontera, declaration.medio_transporte_frontera]);

  const selectedExportacionesTipo = useMemo(() => exportacionesTipos.find(
    (t) => t.codigo === declaration.tipo || t.nombre === declaration.tipo
  ), [exportacionesTipos, declaration.tipo]);

  const selectedTipoUbicacion = useMemo(() => tipoUbicacionOptions.find(
    (t) =>
      t.codigo === declaration.tipo_ubicacion ||
      t.nombre === declaration.tipo_ubicacion
  ), [tipoUbicacionOptions, declaration.tipo_ubicacion]);

  const selectedDivisa = useMemo(() => divisas.find(
    (d) => d.iso === declaration.divisa || d.codigo === declaration.divisa
  ), [divisas, declaration.divisa]);
  
  const selectedDivisaIso = useMemo(() => divisas.find(
    (d) =>
      d.iso === declaration.divisa_iso || d.codigo === declaration.divisa_iso
  ), [divisas, declaration.divisa_iso]);

  const selectedClaveDeclarante = useMemo(() => clavesDeclarante.find(
    (c) =>
      c.codigo === declaration.clave_declarante ||
      c.nombre === declaration.clave_declarante
  ), [clavesDeclarante, declaration.clave_declarante]);

  const selectedDeclaracion1 = useMemo(() => declaraciones1.find(
    (d) =>
      d.codigo === declaration.declaracion_1 ||
      d.nombre === declaration.declaracion_1
  ), [declaraciones1, declaration.declaracion_1]);

  const selectedDeclaracion2 = useMemo(() => declaraciones2.find(
    (d) =>
      d.codigo === declaration.declaracion_2 ||
      d.nombre === declaration.declaracion_2
  ), [declaraciones2, declaration.declaracion_2]);

  const selectedCodigoUN = useMemo(() => codigosUN.find(
    (c) =>
      c.codigo === declaration.codigo_un ||
      c.nombre === declaration.codigo_un
  ), [codigosUN, declaration.codigo_un]);

  const selectedCondicionEntrega = useMemo(() => condicionesEntrega.find(
    (c) => c.codigo === declaration.condiciones_entrega
  ), [condicionesEntrega, declaration.condiciones_entrega]);


  // Track if we've initialized this declaration's fecha_creacion
  const initializedRef = useRef(new Set());

  // Initialize fecha_creacion with current date and time (timestamp) on first load Note: fecha_creacion is already replaced in useDeclarationSummary when data is loaded, so this is just a safety check in case the component renders before that happens Once set, user can edit it and it stays fixed
  useEffect(() => {
    const key = `${declarationIndex}`;
    
    // Only set it once when first loaded if it's missing The main replacement happens in useDeclarationSummary, this is just a fallback
    if (!initializedRef.current.has(key)) {
      // Only set if it's actually missing (useDeclarationSummary should have already set it)
      if (!declaration.fecha_creacion || declaration.fecha_creacion === '') {
        const currentTimestamp = getCurrentTimestamp();
        handleChange(declarationIndex, "fecha_creacion", currentTimestamp);
      }
      initializedRef.current.add(key);
    }
  }, [declarationIndex, handleChange, declaration.fecha_creacion]); // Include fecha_creacion to detect when it's set

  // handlers for selecting a country
  const handlePaisDestinoSelect = (pais) => {
    handleChange(declarationIndex, "pais_destino", pais.codigo);
  };
  const handlePaisExportacionSelect = (pais) => {
    handleChange(declarationIndex, "pais_exportacion", pais.codigo);
  };
  const handlePaisTransporteFronteraSelect = (pais) => {
    handleChange(declarationIndex, "pais_transporte_frontera", pais.codigo);
  };
  // const handlePaisSelect = (pais) => { handleChange(declarationIndex, "pais", pais.codigo); };

  // handlers for selecting an aduana from the modals
  const handleAduanaEntradaSelect = (aduana) => {
    handleChange(declarationIndex, "aduana_entrada", aduana.codigo);
  };
  const handleAduanaSalidaSelect = (aduana) => {
    handleChange(declarationIndex, "aduana_salida", aduana.codigo);
  };
  const handleAduanaDespachoSelect = (aduana) => {
    handleChange(declarationIndex, "aduana_despacho", aduana.codigo);
  };

  // handlers for selecting a modo from the modals
  const handleModoFronteraSelect = (modo) => {
    handleChange(declarationIndex, "modo_transporte_frontera", modo.codigo);
  };
  // const handleModoInteriorSelect = (modo) => { handleChange(declarationIndex, "modo_transporte_interior", modo.codigo); };

  // handlers for selecting a medio from the modals
  const handleMedioFronteraSelect = (medio) => {
    handleChange(declarationIndex, "medio_transporte_frontera", medio.codigo);
  };

  // handler for selecting export type
  const handleExportacionesTipoSelect = (tipo) => {
    handleChange(declarationIndex, "tipo", tipo.codigo);
  };

  const handlePaisUbicacionSelect = (pais) => {
    handleChange(declarationIndex, "pais_ubicacion", pais.codigo);
  };

  // handler for selecting clave declarante
  const handleClaveDeclaranteSelect = (clave) => {
    handleChange(declarationIndex, "clave_declarante", clave.codigo);
  };

  // handler for selecting declaracion 1
  const handleDeclaracion1Select = (declaracion) => {
    handleChange(declarationIndex, "declaracion_1", declaracion.codigo);
  };

  // handler for selecting declaracion 2
  const handleDeclaracion2Select = (declaracion) => {
    handleChange(declarationIndex, "declaracion_2", declaracion.codigo);
  };

  const handleTipoUbicacionSelect = (tipo) => {
    handleChange(declarationIndex, "tipo_ubicacion", tipo.codigo);
  };

  const handleDivisaSelect = (divisa) => {
    handleChange(declarationIndex, "divisa", divisa.codigo);
  };

  const handleDivisaIsoSelect = (divisa) => {
    handleChange(declarationIndex, "divisa_iso", divisa.iso);
  };

  // Tab definitions
  const tabs = [
    { id: "All", label: !isSpanish ? "All" : "Todo" },
    { id: "Expediente", label: !isSpanish ? "Expediente" : "Expediente" },
    { id: "Declaración", label: !isSpanish ? "Declaración" : "Declaración" },
    { id: "Datos Transporte", label: !isSpanish ? "Datos Transporte" : "Datos Transporte" },
    { id: "Información Adicional", label: !isSpanish ? "Información Adicional" : "Información Adicional" },
    { id: "Importes", label: !isSpanish ? "Importes" : "Importes" },
  ];

  return (
    <>
      {/* Tab Navigation */}
      <div className="pt-2 mt-4 overflow-x-auto mb-4">
        <div className="flex gap-2 items-center pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.id
                  ? 'bg-secondary font-semibold px-4'
                  : 'px-2'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="grid grid-cols-2 gap-8 rounded-md">
        {/* EXPEDIENTE TAB */}
        {(activeTab === "All" || activeTab === "Expediente") && (
          <>
            {/* Declaration Type & Date */}
            <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish
              ? "Declaration Type and Date"
              : "Tipo y Fecha de Declaración"}
          </h2>
          <div className={`form-group flex-1`}>
            <label
              htmlFor="tipo"
              className={`${!declaration.tipo && "text-red-600"}`}
            >
              {!isSpanish ? "Type" : "Tipo"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`tipo_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedExportacionesTipo ? "" : "opacity-75"
                }`}
                onClick={() => setExportacionesTipoModalOpen(true)}
              >
                {selectedExportacionesTipo ? (
                  <span className="truncate">
                    {selectedExportacionesTipo.codigo}{" "}
                    {selectedExportacionesTipo.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedExportacionesTipo.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.tipo ? (
                  <span className="truncate" title={declaration.tipo}>
                    {declaration.tipo}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Export Type"
                      : "Seleccionar Tipo de Exportación"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isExportacionesTipoModalOpen && (
                <UniversalSelectModal
                  items={exportacionesTipos}
                  isOpen={true}
                  onClose={() => setExportacionesTipoModalOpen(false)}
                  onSelect={handleExportacionesTipoSelect}
                  selectedItem={selectedExportacionesTipo}
                  title={!isSpanish ? "Select Export Type" : "Seleccionar Tipo de Exportación"}
                  isSpanish={isSpanish}
                  {...modalConfigNameCode}
                />
              )}
            </div>
          </div>
          <div className="form-group flex-1">
            <label
              htmlFor="fecha_registro"
            >
              {!isSpanish ? "Registration Date" : "Fecha Registro"}
            </label>
            <OptimizedInput
              id={`fecha_registro_${declarationIndex + 1}`}
              value={declaration.fecha_registro}
              placeholder={fechaRegistroPlaceholder}
              onChange={handleFechaRegistro}
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          <div className="form-group flex-1">
            <label
              htmlFor="fecha_creacion"
            >
              {!isSpanish ? "Creation Date" : "Fecha Creación"}
            </label>
            <OptimizedInput
              id={`fecha_creacion_${declarationIndex + 1}`}
              type="text"
              value={declaration.fecha_creacion || ""}
              placeholder="YYYY-MM-DDTHH:MM:SS"
              onChange={handleFechaCreacion}
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          {/* Fecha Oper. - Missing field from PDF */}
          <div className="form-group flex-1">
            <label
              htmlFor="fecha_operacion"
            >
              {!isSpanish ? "Operation Date" : "Fecha Operación"}
            </label>
            <OptimizedInput
              id={`fecha_operacion_${declarationIndex + 1}`}
              type="text"
              value={declaration.fecha_operacion || ""}
              placeholder="YYYY-MM-DDTHH:MM:SS"
              onChange={handleFechaOperacion}
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
        </div>

        {/* Declaration Identification */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish
              ? "Declaration Identification"
              : "Identificación de la Declaración"}
          </h2>
          {/* MRN */}
          <div className="form-group flex-1">
            <label
              htmlFor="mrn"
            >
              {!isSpanish ? "MRN" : "MRN (Número de Referencia del Movimiento)"}
            </label>
            <OptimizedInput
              id={`mrn_${declarationIndex + 1}`}
              value={declaration.mrn || ""}
              placeholder={mrnPlaceholder}
              onChange={handleMrn}
              className="w-full border rounded px-2 py-1 pr-8"
              maxLength={35}
            />
          </div>
          <div className="form-group flex-1">
            <label
              htmlFor="declaracion_1"
            >
              {!isSpanish ? "Declaration 1" : "Declaración 1"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`declaracion_1_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedDeclaracion1 ? "" : "opacity-75"
                }`}
                onClick={() => setDeclaracion1ModalOpen(true)}
              >
                {selectedDeclaracion1 ? (
                  <span
                    className="truncate"
                    style={{ maxWidth: "200px", display: "inline-block" }}
                    title={`${selectedDeclaracion1.codigo} - ${selectedDeclaracion1.nombre}`}
                  >
                    {selectedDeclaracion1.codigo}{" "}
                    {selectedDeclaracion1.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedDeclaracion1.nombre.substring(0, 50)}...)
                      </span>
                    )}
                  </span>
                ) : declaration.declaracion_1 ? (
                  <span
                    className="truncate"
                    title={declaration.declaracion_1}
                  >
                    {declaration.declaracion_1}
                  </span>
                ) : (
                  <span
                    className="truncate"
                    title={
                      !isSpanish
                        ? "Select Declaration 1"
                        : "Seleccionar Declaración 1"
                    }
                  >
                    {!isSpanish
                      ? "Select Declaration 1"
                      : "Seleccionar Declaración 1"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isDeclaracion1ModalOpen && (
                <UniversalSelectModal
                  items={declaraciones1}
                  isOpen={true}
                  onClose={() => setDeclaracion1ModalOpen(false)}
                  onSelect={handleDeclaracion1Select}
                  selectedItem={selectedDeclaracion1}
                  title={!isSpanish ? "Select Declaration 1" : "Seleccionar Declaración 1"}
                  isSpanish={isSpanish}
                  {...modalConfigCodeName}
                  searchPlaceholderType="search_declaration"
                />
              )}
            </div>
          </div>
          <div className="form-group flex-1">
            <label
              htmlFor="declaracion_2"
            >
              {!isSpanish ? "Declaration 2" : "Declaración 2"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`declaracion_2_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedDeclaracion2 ? "" : "opacity-75"
                }`}
                onClick={() => setDeclaracion2ModalOpen(true)}
              >
                {selectedDeclaracion2 ? (
                  <span
                    className="truncate"
                    style={{ maxWidth: "200px", display: "inline-block" }}
                    title={`${selectedDeclaracion2.codigo} - ${selectedDeclaracion2.nombre}`}
                  >
                    {selectedDeclaracion2.codigo}{" "}
                    {selectedDeclaracion2.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedDeclaracion2.nombre.substring(0, 50)}...)
                      </span>
                    )}
                  </span>
                ) : declaration.declaracion_2 ? (
                  <span
                    className="truncate"
                    title={declaration.declaracion_2}
                  >
                    {declaration.declaracion_2}
                  </span>
                ) : (
                  <span
                    className="truncate"
                    title={
                      !isSpanish
                        ? "Select Declaration 2"
                        : "Seleccionar Declaración 2"
                    }
                  >
                    {!isSpanish
                      ? "Select Declaration 2"
                      : "Seleccionar Declaración 2"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isDeclaracion2ModalOpen && (
                <UniversalSelectModal
                  items={declaraciones2}
                  isOpen={true}
                  onClose={() => setDeclaracion2ModalOpen(false)}
                  onSelect={handleDeclaracion2Select}
                  selectedItem={selectedDeclaracion2}
                  title={!isSpanish ? "Select Declaration 2" : "Seleccionar Declaración 2"}
                  isSpanish={isSpanish}
                  {...modalConfigCodeName}
                  searchPlaceholderType="search_declaration"
                />
              )}
            </div>
          </div>
          <div className="form-group flex-1">
            <label
              htmlFor="declaracion_3"
            >
              {!isSpanish ? "Declaration 3" : "Declaración 3"}
            </label>
            <OptimizedInput
              id={`declaracion_3_${declarationIndex + 1}`}
              value={declaration.declaracion_3}
              placeholder={declaracion3Placeholder}
              onChange={handleDeclaracion3}
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
        </div>

        {/* Operators/Parties References */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish ? "Operators Information" : "Información de Operadores"}
          </h2>
          <div className="form-group">
            <label
              htmlFor="cliente_id"
              className={`${!declaration.cliente_id && "text-red-600"}`}
            >
              {!isSpanish ? "Client ID" : "Cliente ID"}
            </label>
            <OptimizedInput
              id={`cliente_id_${declarationIndex + 1}`}
              value={declaration.cliente_id}
              placeholder={clienteIdPlaceholder}
              onChange={handleClienteId}
              className={`w-full border rounded px-2 py-1 pr-8 ${
                !declaration.cliente_id && "required-input"
              }`}
              required
            />
          </div>
          <div className="form-group">
            <label
              htmlFor="remitente_id"
              className={`${!declaration.remitente_id && "text-red-600"}`}
            >
              {!isSpanish ? "Sender ID" : "Remitente ID"}
            </label>
            <OptimizedInput
              id={`remitente_id_${declarationIndex + 1}`}
              value={declaration.remitente_id}
              placeholder={remitenteIdPlaceholder}
              onChange={handleRemitenteId}
              className={`w-full border rounded px-2 py-1 pr-8 ${
                !declaration.remitente_id && "required-input"
              }`}
              required
            />
          </div>
          {/* Clave Declarante - Missing field from PDF */}
          <div className="form-group">
            <label
              htmlFor="clave_declarante"
            >
              {!isSpanish ? "Declarant Key" : "Clave Declarante"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`clave_declarante_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedClaveDeclarante ? "" : "opacity-75"
                }`}
                onClick={() => setClaveDeclaranteModalOpen(true)}
              >
                {selectedClaveDeclarante ? (
                  <span
                    className="truncate"
                    style={{ maxWidth: "200px", display: "inline-block" }}
                    title={`${selectedClaveDeclarante.codigo} - ${selectedClaveDeclarante.nombre}`}
                  >
                    {selectedClaveDeclarante.codigo}{" "}
                    {selectedClaveDeclarante.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedClaveDeclarante.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.clave_declarante ? (
                  <span
                    className="truncate"
                    title={declaration.clave_declarante}
                  >
                    {declaration.clave_declarante}
                  </span>
                ) : (
                  <span
                    className="truncate"
                    title={
                      !isSpanish
                        ? "Select Declarant Key"
                        : "Seleccionar Clave Declarante"
                    }
                  >
                    {!isSpanish
                      ? "Select Declarant Key"
                      : "Seleccionar Clave Declarante"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isClaveDeclaranteModalOpen && (
                <UniversalSelectModal
                  items={clavesDeclarante}
                  isOpen={true}
                  onClose={() => setClaveDeclaranteModalOpen(false)}
                  onSelect={handleClaveDeclaranteSelect}
                  selectedItem={selectedClaveDeclarante}
                  title={!isSpanish ? "Select Declarant Key" : "Seleccionar Clave Declarante"}
                  isSpanish={isSpanish}
                  {...modalConfigCodeName}
                />
              )}
            </div>
          </div>
        </div>

        {/* Packages */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish ? "Packaging Information" : "Información de Embalaje"}
          </h2>
          <div className="form-group">
            <label
              htmlFor="tiene_contenedores"
            >
              {!isSpanish ? "Has Containers" : "Tiene Contenedores"}
            </label>
            <OptimizedInput
              id={`tiene_contenedores_${declarationIndex + 1}`}
              value={declaration.tiene_contenedores}
              placeholder={tieneContenedoresPlaceholder}
              onChange={handleTieneContenedores}
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          <div className="form-group">
            <label
              htmlFor="total_bultos"
            >
              {!isSpanish ? "Total Packages" : "Total Bultos"}
            </label>
            <OptimizedInput
              id={`total_bultos_${declarationIndex + 1}`}
              value={declaration.total_bultos}
              placeholder={totalBultosPlaceholder}
              onChange={handleTotalBultos}
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
        </div>
          </>
        )}

        {/* DECLARACIÓN TAB */}
        {(activeTab === "All" || activeTab === "Declaración") && (
          <>
        {/* Customs Information */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish ? "Customs Information" : "Información Aduanera"}
          </h2>
          {/* aduana_entrada custom selector */}
          <div className="form-group">
            <label
              htmlFor="aduana_entrada"
            >
              {!isSpanish ? "Entry Customs" : "Aduana Entrada"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`aduana_entrada_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedAduanaEntrada ? "" : "opacity-75"
                }`}
                onClick={() => setAduanaEntradaModalOpen(true)}
              >
                {selectedAduanaEntrada ? (
                  <span className="truncate">
                    {selectedAduanaEntrada.codigo}{" "}
                    {selectedAduanaEntrada.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedAduanaEntrada.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.aduana_entrada ? (
                  <span className="truncate" title={declaration.aduana_entrada}>
                    {declaration.aduana_entrada}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Entry Customs"
                      : "Seleccionar Aduana Entrada"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isAduanaEntradaModalOpen && (
                <UniversalSelectModal
                  items={aduanasEntrada}
                  isOpen={true}
                  onClose={() => setAduanaEntradaModalOpen(false)}
                  onSelect={handleAduanaEntradaSelect}
                  selectedItem={selectedAduanaEntrada}
                  title={!isSpanish ? "Select Entry Customs" : "Seleccionar Aduana Entrada"}
                  isSpanish={isSpanish}
                  {...modalConfigNameCode}
                />
              )}
            </div>
          </div>
          {/* aduana_salida custom selector */}
          <div className="form-group">
            <label
              htmlFor="aduana_salida"
            >
              {!isSpanish ? "Exit Customs" : "Aduana Salida"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`aduana_salida_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedAduanaSalida ? "" : "opacity-75"
                }`}
                onClick={() => setAduanaSalidaModalOpen(true)}
              >
                {selectedAduanaSalida ? (
                  <span className="truncate">
                    {selectedAduanaSalida.codigo}{" "}
                    {selectedAduanaSalida.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedAduanaSalida.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.aduana_salida ? (
                  <span className="truncate" title={declaration.aduana_salida}>
                    {declaration.aduana_salida}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Exit Customs"
                      : "Seleccionar Aduana Salida"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isAduanaSalidaModalOpen && (
                <UniversalSelectModal
                  items={aduanasSalida}
                  isOpen={true}
                  onClose={() => setAduanaSalidaModalOpen(false)}
                  onSelect={handleAduanaSalidaSelect}
                  selectedItem={selectedAduanaSalida}
                  title={!isSpanish ? "Select Exit Customs" : "Seleccionar Aduana Salida"}
                  isSpanish={isSpanish}
                  {...modalConfigNameCode}
                />
              )}
            </div>
          </div>
          {/* aduana_despacho custom selector */}
          <div className="form-group">
            <label
              htmlFor="aduana_despacho"
            >
              {!isSpanish ? "Clearance Customs" : "Aduana Despacho"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`aduana_despacho_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedAduanaDespacho ? "" : "opacity-75"
                }`}
                onClick={() => setAduanaDespachoModalOpen(true)}
              >
                {selectedAduanaDespacho ? (
                  <span className="truncate">
                    {selectedAduanaDespacho.codigo}{" "}
                    {selectedAduanaDespacho.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedAduanaDespacho.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.aduana_despacho ? (
                  <span
                    className="truncate"
                    title={declaration.aduana_despacho}
                  >
                    {declaration.aduana_despacho}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Clearance Customs"
                      : "Seleccionar Aduana Despacho"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isAduanaDespachoModalOpen && (
                <UniversalSelectModal
                  items={aduanasDespacho}
                  isOpen={true}
                  onClose={() => setAduanaDespachoModalOpen(false)}
                  onSelect={handleAduanaDespachoSelect}
                  selectedItem={selectedAduanaDespacho}
                  title={!isSpanish ? "Select Clearance Customs" : "Seleccionar Aduana Despacho"}
                  isSpanish={isSpanish}
                  {...modalConfigNameCode}
                />
              )}
            </div>
          </div>
        </div>
          </>
        )}

        {/* DATOS TRANSPORTE TAB */}
        {(activeTab === "All" || activeTab === "Datos Transporte") && (
          <>
        {/* Transport */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish ? "Transport Details" : "Detalles de Transporte"}
          </h2>
          <div className="form-group">
            <label
              htmlFor="modo_transporte_frontera"
            >
              {!isSpanish
                ? "Border Transport Mode"
                : "Modo Transporte Frontera"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`modo_transporte_frontera_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedModoFrontera ? "" : "opacity-75"
                }`}
                onClick={() => setModoFronteraModalOpen(true)}
              >
                {selectedModoFrontera ? (
                  <span
                    className="truncate"
                    style={{ maxWidth: "200px", display: "inline-block" }}
                    title={`${selectedModoFrontera.codigo} - ${selectedModoFrontera.nombre}`}
                  >
                    {selectedModoFrontera.codigo}{" "}
                    {selectedModoFrontera.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedModoFrontera.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.modo_transporte_frontera ? (
                  <span
                    className="truncate"
                    title={declaration.modo_transporte_frontera}
                  >
                    {declaration.modo_transporte_frontera}
                  </span>
                ) : (
                  <span
                    className="truncate"
                    title={
                      !isSpanish
                        ? "Select Border Transport Mode"
                        : "Seleccionar Modo Transporte Frontera"
                    }
                  >
                    {!isSpanish
                      ? "Select Border Transport Mode"
                      : "Seleccionar Modo Transporte Frontera"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isModoFronteraModalOpen && (
                <UniversalSelectModal
                  items={modosTransporteFronteraPredefinidos}
                  isOpen={true}
                  onClose={() => setModoFronteraModalOpen(false)}
                  onSelect={handleModoFronteraSelect}
                  selectedItem={selectedModoFrontera}
                  title={!isSpanish ? "Select Border Transport Mode" : "Seleccionar Modo Transporte Frontera"}
                  isSpanish={isSpanish}
                  {...modalConfigNameCode}
                />
              )}
            </div>
          </div>
          <div className="form-group">
            <label
              htmlFor="modo_transporte_interior"
            >
              {!isSpanish
                ? "Internal Transport Mode"
                : "Modo Transporte Interior"}
            </label>
            <OptimizedInput
              id={`modo_transporte_interior_${declarationIndex + 1}`}
              value={declaration.modo_transporte_interior}
              placeholder={modoTransporteInteriorPlaceholder}
              onChange={handleModoTransporteInterior}
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          <div className="form-group flex-1">
            <label
              htmlFor="identidad_transporte_frontera"
            >
              {!isSpanish
                ? "Border Transport Identity"
                : "Identidad Transporte Frontera"}
            </label>
            <OptimizedInput
              id={`identidad_transporte_frontera_${declarationIndex + 1}`}
              value={declaration.identidad_transporte_frontera}
              placeholder={identidadTransporteFronteraPlaceholder}
              onChange={handleIdentidadTransporteFrontera}
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          {/* Medio Transporte Frontera - Missing field from PDF (separate from modo_transporte_frontera) */}
          <div className="form-group">
            <label
              htmlFor="medio_transporte_frontera"
            >
              {!isSpanish
                ? "Transport Means (Border)"
                : "Medio Transporte Frontera"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`medio_transporte_frontera_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedMedioTransporteFrontera ? "" : "opacity-75"
                }`}
                onClick={() => setMedioFronteraModalOpen(true)}
              >
                {selectedMedioTransporteFrontera ? (
                  <span
                    className="truncate"
                    style={{ maxWidth: "200px", display: "inline-block" }}
                    title={`${selectedMedioTransporteFrontera.codigo} - ${selectedMedioTransporteFrontera.nombre}`}
                  >
                    {selectedMedioTransporteFrontera.codigo}{" "}
                    {selectedMedioTransporteFrontera.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedMedioTransporteFrontera.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.medio_transporte_frontera ? (
                  <span
                    className="truncate"
                    title={declaration.medio_transporte_frontera}
                  >
                    {declaration.medio_transporte_frontera}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Transport Means"
                      : "Seleccionar Medio Transporte Frontera"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isMedioFronteraModalOpen && (
                <UniversalSelectModal
                  items={mediosTransporteFrontera}
                  isOpen={true}
                  onClose={() => setMedioFronteraModalOpen(false)}
                  onSelect={handleMedioFronteraSelect}
                  selectedItem={selectedMedioTransporteFrontera}
                  title={!isSpanish ? "Select Transport Means" : "Seleccionar Medio Transporte Frontera"}
                  isSpanish={isSpanish}
                  {...modalConfigNameCode}
                />
              )}
            </div>
          </div>
        </div>
          </>
        )}

        {/* INFORMACIÓN ADICIONAL TAB */}
        {(activeTab === "All" || activeTab === "Información Adicional") && (
          <>
        {/* EDI Status */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish ? "EDI Status" : "Estado EDI"}
          </h2>
          <div className="form-group">
            <label htmlFor="autorizacion_por_operacion">
              {!isSpanish
                ? "Authorization per Operation (0/1)"
                : "Autorización por Operación (0/1)"}
            </label>
            <BinaryToggle
              id={`autorizacion_por_operacion_${declarationIndex + 1}`}
              value={declaration.autorizacion_por_operacion}
              onChange={handleAutorizacionPorOperacion}
            />
          </div>
         
          {/* Datos de Seguridad - Missing field from PDF */}
          <div className="form-group">
            <label htmlFor="flag_datos_seguridad">
              {!isSpanish
                ? "Security Data (0/1)"
                : "Datos de Seguridad (0/1)"}
            </label>
            <BinaryToggle
              id={`flag_datos_seguridad_${declarationIndex + 1}`}
              value={declaration.flag_datos_seguridad}
              onChange={handleFlagDatosSeguridad}
            />
          </div>
          <div className="form-group">
            <label htmlFor="es_simplificado">
              {!isSpanish ? "Simplified (0/1)" : "Simplificado (0/1)"}
            </label>
            <BinaryToggle
              id={`es_simplificado_${declarationIndex + 1}`}
              value={declaration.es_simplificado}
              onChange={handleEsSimplificado}
            />
          </div>
          <div className="form-group">
            <label htmlFor="es_recapitulativo">
              {!isSpanish ? "Recapitulative (0/1)" : "Recapitulativo (0/1)"}
            </label>
            <BinaryToggle
              id={`es_recapitulativo_${declarationIndex + 1}`}
              value={declaration.es_recapitulativo}
              onChange={handleEsRecapitulativo}
            />
          </div>
           {/* New: Status/Authorization fields */}
           <div className="form-group">
            <label htmlFor="estado_pdc">
              {!isSpanish ? "PDC Status" : "Estado PDC"}
            </label>
            <OptimizedInput
              id={`estado_pdc_${declarationIndex + 1}`}
              value={declaration.estado_pdc ?? ""}
              type="number"
              placeholder={example0Placeholder}
              onChange={handleEstadoPdc}
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          <div className="form-group">
            <label
              htmlFor="estado_edi"
            >
              {!isSpanish ? "EDI Status" : "Estado EDI"}
            </label>
            <OptimizedInput
              id={`estado_edi_${declarationIndex + 1}`}
              value={declaration.estado_edi}
              type="number"
              placeholder={estadoEdiPlaceholder}
              onChange={handleEstadoEdi}
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          <div className="form-group">
            <label
              htmlFor="estado_edi_canarias"
            >
              {!isSpanish ? "Canary Islands EDI Status" : "Estado EDI Canarias"}
            </label>
            <OptimizedInput
              id={`estado_edi_canarias_${declarationIndex + 1}`}
              value={declaration.estado_edi_canarias}
              type="number"
              placeholder={estadoEdiCanariasPlaceholder}
              onChange={handleEstadoEdiCanarias}
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
        </div>
        {/* Other Information */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish ? "Additional Information" : "Información Adicional"}
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="form-group ">
              <label htmlFor={`completo_${declarationIndex + 1}`}>
                {!isSpanish ? "Complete (0/1)" : "Completo (0/1)"}
              </label>
              <BinaryToggle
                id={`completo_${declarationIndex + 1}`}
                value={declaration.completo}
                onChange={handleCompleto}
              />
            </div>
            <div className="form-group">
              <label htmlFor={`es_plantilla_${declarationIndex + 1}`}>
                {!isSpanish ? "Is Template (0/1)" : "¿Es Plantilla (0/1)?"}
              </label>
              <BinaryToggle
                id={`es_plantilla_${declarationIndex + 1}`}
                value={declaration.es_plantilla}
                onChange={handleEsPlantilla}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="form-group">
              <label htmlFor="e_mail">
                {!isSpanish
                  ? "Contact Email"
                  : "Correo Electrónico de Contacto"}
              </label>
              <OptimizedInput
                id={`e_mail_${declarationIndex + 1}`}
                value={declaration.e_mail || ""}
                placeholder={exampleEmailPlaceholder}
              onChange={handleEmail}
                className="w-full border rounded px-2 py-1 pr-8"
                maxLength={70}
              />
            </div>
            <div className="form-group">
              <label htmlFor="contacto_telefono">
                {!isSpanish ? "Contact Phone" : "Teléfono de Contacto"}
              </label>
              <OptimizedInput
                id={`contacto_telefono_${declarationIndex + 1}`}
                value={declaration.contacto_telefono || ""}
                placeholder={example916769999Placeholder}
                onChange={handleContactoTelefono}
                className="w-full border rounded px-2 py-1 pr-8"
                maxLength={35}
              />
            </div>
          </div>
          <div className="form-group">
            <label
              htmlFor="naturaleza_transaccion"
            >
              {!isSpanish ? "Transaction Nature" : "Naturaleza Transacción"}
            </label>
            <OptimizedInput
              id={`naturaleza_transaccion_${declarationIndex + 1}`}
              value={declaration.naturaleza_transaccion}
              placeholder={naturalezaTransaccionPlaceholder}
              onChange={handleNaturalezaTransaccion}
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          <div className="form-group">
            <label
              htmlFor="observaciones"
            >
              {!isSpanish ? "Observations" : "Observaciones"}
            </label>
            <OptimizedInput
              id={`observaciones_${declarationIndex + 1}`}
              value={declaration.observaciones}
              placeholder={observacionesPlaceholder}
              onChange={handleObservaciones}
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
        </div>
          </>
        )}

        {/* IMPORTES TAB */}
        {(activeTab === "All" || activeTab === "Importes") && (
          <>
        {/* Invoice Information */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1 ">
          <h2 className="text-sm font-bold">
            {!isSpanish ? "Invoice Details" : "Detalles de Factura"}
          </h2>
          {/* New: Currency fields */}
          <div className="form-group">
            <label htmlFor="divisa">
              {!isSpanish
                ? "Currency Code (numeric)"
                : "Código Divisa (numérico)"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`divisa_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedDivisa ? "" : "opacity-75"
                }`}
                onClick={() => setDivisaModalOpen(true)}
              >
                {selectedDivisa ? (
                  <span className="truncate">
                    {selectedDivisa.codigo}{" "}
                    <span className="text-xs text-gray-400 ml-2">
                      ({selectedDivisa.descripcion})
                    </span>
                  </span>
                ) : declaration.divisa ? (
                  <span className="truncate" title={declaration.divisa}>
                    {declaration.divisa}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Currency Code"
                      : "Seleccionar Código Divisa"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isDivisaModalOpen && (
                <UniversalSelectModal
                  items={divisas}
                  isOpen={true}
                  onClose={() => setDivisaModalOpen(false)}
                  onSelect={handleDivisaSelect}
                  selectedItem={selectedDivisa}
                  title={!isSpanish ? "Select Currency Code" : "Seleccionar Código Divisa"}
                  isSpanish={isSpanish}
                  {...modalConfigCodeDesc}
                />
              )}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="cambio_divisa">
              {!isSpanish ? "Exchange Rate" : "Cambio Divisa"}
            </label>
            <OptimizedInput
              id={`cambio_divisa_${declarationIndex + 1}`}
              value={declaration.cambio_divisa || ""}
              placeholder={example1_0Placeholder}
              onChange={handleCambioDivisa}
              className="w-full border rounded px-2 py-1 pr-8"
              maxLength={10}
            />
          </div>
          <div className="form-group">
            <label htmlFor="divisa_iso">
              {!isSpanish ? "Currency ISO (3-letter)" : "Divisa ISO (3 letras)"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`divisa_iso_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedDivisaIso ? "" : "opacity-75"
                }`}
                onClick={() => setDivisaIsoModalOpen(true)}
              >
                {selectedDivisaIso ? (
                  <span className="truncate">
                    {selectedDivisaIso.iso}{" "}
                    <span className="text-xs text-gray-400 ml-2">
                      ({selectedDivisaIso.descripcion})
                    </span>
                  </span>
                ) : declaration.divisa_iso ? (
                  <span className="truncate" title={declaration.divisa_iso}>
                    {declaration.divisa_iso}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Currency ISO"
                      : "Seleccionar Divisa ISO"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isDivisaIsoModalOpen && (
                <UniversalSelectModal
                  items={divisas}
                  isOpen={true}
                  onClose={() => setDivisaIsoModalOpen(false)}
                  onSelect={handleDivisaIsoSelect}
                  selectedItem={selectedDivisaIso}
                  title={!isSpanish ? "Select Currency ISO" : "Seleccionar Divisa ISO"}
                  isSpanish={isSpanish}
                  {...modalConfigIsoDesc}
                />
              )}
            </div>
          </div>
          <div className="form-group">
            <label
              htmlFor="factura"
            >
              {!isSpanish ? "Invoice" : "Factura"}
            </label>
            <OptimizedInput
              id={`factura_${declarationIndex + 1}`}
              value={declaration.factura}
              placeholder={facturaPlaceholder}
              onChange={handleFactura}
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          <div className="form-group">
            <label
              htmlFor="fecha_factura"
            >
              {!isSpanish ? "Invoice Date" : "Fecha Factura"}
            </label>
            <OptimizedInput
              id={`fecha_factura_${declarationIndex + 1}`}
              value={declaration.fecha_factura}
              placeholder={fechaFacturaPlaceholder}
              onChange={handleFechaFactura}
              className="w-full border rounded px-2 py-1 pr-8"
            />
          </div>
          <div className="form-group">
            <label
              htmlFor="condiciones_entrega"
            >
              {!isSpanish ? "Delivery Terms" : "Condiciones Entrega"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  declaration.condiciones_entrega ? "" : "opacity-75"
                }`}
                onClick={() => setCondicionesEntregaModalOpen(true)}
                id={`condiciones_entrega_${declarationIndex + 1}`}
              >
                {selectedCondicionEntrega ? (
                      <span className="truncate">
                    {selectedCondicionEntrega.codigo}{" "}
                        <span className="text-xs text-gray-400 ml-2">
                      ({selectedCondicionEntrega.nombre})
                        </span>
                      </span>
                ) : declaration.condiciones_entrega ? (
                      <span
                        className="truncate"
                        title={declaration.condiciones_entrega}
                      >
                        {declaration.condiciones_entrega}
                      </span>
                ) : (
                      <span className="truncate">
                        {!isSpanish
                          ? "Select Delivery Terms"
                          : "Seleccionar Condición de Entrega"}
                      </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isCondicionesEntregaModalOpen && (
                <UniversalSelectModal
                  items={condicionesEntrega}
                  isOpen={true}
                  onClose={() => setCondicionesEntregaModalOpen(false)}
                  onSelect={(cond) => {
                    handleChange(declarationIndex, "condiciones_entrega", cond.codigo);
                    setCondicionesEntregaModalOpen(false);
                  }}
                  selectedItem={selectedCondicionEntrega}
                  title={!isSpanish ? "Select Delivery Terms" : "Seleccionar Condición de Entrega"}
                  isSpanish={isSpanish}
                  {...modalConfigCodeName}
                />
              )}
            </div>
          </div>
        </div>
        {/* Countries */}
        <div className="split-modal flex flex-col gap-4 rounded-md flex-1">
          <h2 className="text-sm font-bold">
            {!isSpanish ? "Country Information" : "Información de Países"}
          </h2>
          {/* New: Location fields */}
          <div className="form-group">
            <label htmlFor="pais_ubicacion">
              {!isSpanish
                ? "Location Country (2-letter ISO)"
                : "País Ubicación (ISO 2 letras)"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`pais_ubicacion_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedPaisUbicacion ? "" : "opacity-75"
                }`}
                onClick={() => setPaisUbicacionModalOpen(true)}
              >
                {selectedPaisUbicacion ? (
                  <span className="truncate">
                    {selectedPaisUbicacion.codigo}{" "}
                    {selectedPaisUbicacion.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedPaisUbicacion.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.pais_ubicacion ? (
                  <span className="truncate" title={declaration.pais_ubicacion}>
                    {declaration.pais_ubicacion}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Location Country"
                      : "Seleccionar País Ubicación"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isPaisUbicacionModalOpen && (
                <UniversalSelectModal
                  items={paises}
                  isOpen={true}
                  onClose={() => setPaisUbicacionModalOpen(false)}
                  onSelect={handlePaisUbicacionSelect}
                  selectedItem={selectedPaisUbicacion}
                  title={!isSpanish ? "Select Location Country" : "Seleccionar País Ubicación"}
                  isSpanish={isSpanish}
                  {...modalConfigNameOnly}
                />
              )}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="tipo_ubicacion">
              {!isSpanish
                ? "Location Type (A/B/C/D)"
                : "Tipo Ubicación (A/B/C/D)"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`tipo_ubicacion_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedTipoUbicacion ? "" : "opacity-75"
                }`}
                onClick={() => setTipoUbicacionModalOpen(true)}
              >
                {selectedTipoUbicacion ? (
                  <span className="truncate">
                    {selectedTipoUbicacion.codigo}{" "}
                    {selectedTipoUbicacion.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedTipoUbicacion.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.tipo_ubicacion ? (
                  <span className="truncate" title={declaration.tipo_ubicacion}>
                    {declaration.tipo_ubicacion}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Location Type"
                      : "Seleccionar Tipo de Ubicación"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isTipoUbicacionModalOpen && (
                <UniversalSelectModal
                  items={tipoUbicacionOptions}
                  isOpen={true}
                  onClose={() => setTipoUbicacionModalOpen(false)}
                  onSelect={handleTipoUbicacionSelect}
                  selectedItem={selectedTipoUbicacion}
                  title={!isSpanish ? "Select Location Type" : "Seleccionar Tipo de Ubicación"}
                  isSpanish={isSpanish}
                  {...modalConfigNameCode}
                />
              )}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="localizacion">
              {!isSpanish ? "Location Code" : "Código de Localización"}
            </label>
            <OptimizedInput
              id={`localizacion_${declarationIndex + 1}`}
              value={declaration.localizacion || ""}
              placeholder={example280112345Placeholder}
              onChange={handleLocalizacion}
              className="w-full border rounded px-2 py-1 pr-8"
              maxLength={35}
            />
          </div>
          {/* Lugar Entrega - Missing fields from PDF */}
          <div className="form-group">
            <label htmlFor="codigo_un">
              {!isSpanish ? "UN Code (Delivery Place)" : "Código UN (Lugar Entrega)"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`codigo_un_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedCodigoUN ? "" : "opacity-75"
                }`}
                onClick={() => setCodigoUNModalOpen(true)}
              >
                {selectedCodigoUN ? (
                  <span className="truncate">
                    {selectedCodigoUN.codigo}{" "}
                    {selectedCodigoUN.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedCodigoUN.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.codigo_un ? (
                  <span className="truncate" title={declaration.codigo_un}>
                    {declaration.codigo_un}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select UN Code"
                      : "Seleccionar Código UN"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isCodigoUNModalOpen && (
                <UniversalSelectModal
                  items={codigosUN}
                  isOpen={true}
                  onClose={() => setCodigoUNModalOpen(false)}
                  onSelect={(codigoUN) => {
                    handleChange(declarationIndex, "codigo_un", codigoUN.codigo);
                    setCodigoUNModalOpen(false);
                  }}
                  selectedItem={selectedCodigoUN}
                  title={!isSpanish ? "Select UN Code" : "Seleccionar Código UN"}
                  isSpanish={isSpanish}
                  {...modalConfigNameCode}
                />
              )}
            </div>
          </div>
          {/* <div className="form-group">
            <label htmlFor="lugar_entrega">
              {!isSpanish ? "Delivery Place" : "Lugar Entrega"}
            </label>
            <OptimizedInput
              id={`lugar_entrega_${declarationIndex + 1}`}
              value={declaration.lugar_entrega || ""}
              placeholder={exampleSurreyPlaceholder}
              onChange={getFieldHandler("lugar_entrega")}
              className="w-full border rounded px-2 py-1 pr-8"
              maxLength={70}
            />
          </div> */}
          {/* Tipo Autorización C.D. and Ident. Adicional - Missing fields from PDF */}
          <div className="form-group">
            <label htmlFor="tipo_autorizacion_cd">
              {!isSpanish
                ? "Type (49 Autorización C.D.)"
                : "Tipo (49 Autorización C.D.)"}
            </label>
            <OptimizedInput
              id={`tipo_autorizacion_cd_${declarationIndex + 1}`}
              value={declaration.tipo_autorizacion_cd || ""}
              placeholder={tipoAutorizacionCdPlaceholder}
              onChange={handleTipoAutorizacionCd}
              className="w-full border rounded px-2 py-1 pr-8"
              maxLength={10}
            />
          </div>
          <div className="form-group">
            <label htmlFor="identificacion_adicional">
              {!isSpanish
                ? "Additional Identification"
                : "Identificación Adicional"}
            </label>
            <OptimizedInput
              id={`identificacion_adicional_${declarationIndex + 1}`}
              value={declaration.identificacion_adicional || ""}
              placeholder={identificacionAdicionalPlaceholder}
              onChange={handleIdentificacionAdicional}
              className="w-full border rounded px-2 py-1 pr-8"
              maxLength={35}
            />
          </div>
          <div className="form-group">
            <label
              htmlFor="pais_destino"
            >
              {!isSpanish ? "Destination Country" : "País Destino"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`pais_destino_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedPaisDestino ? "" : "opacity-75"
                }`}
                onClick={() => setPaisDestinoModalOpen(true)}
              >
                {selectedPaisDestino ? (
                  <span className="truncate">
                    {selectedPaisDestino.codigo}{" "}
                    {selectedPaisDestino.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedPaisDestino.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.pais_destino ? (
                  <span className="truncate" title={declaration.pais_destino}>
                    {declaration.pais_destino}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Destination Country"
                      : "Seleccionar País Destino"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isPaisDestinoModalOpen && (
                <UniversalSelectModal
                  items={paises}
                  isOpen={true}
                  onClose={() => setPaisDestinoModalOpen(false)}
                  onSelect={handlePaisDestinoSelect}
                  selectedItem={selectedPaisDestino}
                  title={!isSpanish ? "Select Destination Country" : "Seleccionar País Destino"}
                  isSpanish={isSpanish}
                  {...modalConfigNameOnly}
                />
              )}
            </div>
          </div>
          <div className="form-group">
            <label
              htmlFor="pais_exportacion"
            >
              {!isSpanish ? "Export Country" : "País Exportación"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`pais_exportacion_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedPaisExportacion ? "" : "opacity-75"
                }`}
                onClick={() => setPaisExportacionModalOpen(true)}
              >
                {selectedPaisExportacion ? (
                  <span className="truncate">
                    {selectedPaisExportacion.codigo}{" "}
                    {selectedPaisExportacion.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedPaisExportacion.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.pais_exportacion ? (
                  <span
                    className="truncate"
                    title={declaration.pais_exportacion}
                  >
                    {declaration.pais_exportacion}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Export Country"
                      : "Seleccionar País Exportación"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isPaisExportacionModalOpen && (
                <UniversalSelectModal
                  items={paises}
                  isOpen={true}
                  onClose={() => setPaisExportacionModalOpen(false)}
                  onSelect={handlePaisExportacionSelect}
                  selectedItem={selectedPaisExportacion}
                  title={!isSpanish ? "Select Export Country" : "Seleccionar País Exportación"}
                  isSpanish={isSpanish}
                  {...modalConfigNameOnly}
                />
              )}
            </div>
          </div>
          <div className="form-group">
            <label
              htmlFor="pais_transporte_frontera"
            >
              {!isSpanish
                ? "Border Transport Country"
                : "País Transporte Frontera"}
            </label>
            <div className="relative w-full">
              <button
                type="button"
                id={`pais_transporte_frontera_${declarationIndex + 1}`}
                className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${
                  selectedPaisTransporteFrontera ? "" : "opacity-75"
                }`}
                onClick={() => setPaisTransporteFronteraModalOpen(true)}
              >
                {selectedPaisTransporteFrontera ? (
                  <span className="truncate">
                    {selectedPaisTransporteFrontera.codigo}{" "}
                    {selectedPaisTransporteFrontera.nombre && (
                      <span className="text-xs text-gray-400 ml-2">
                        ({selectedPaisTransporteFrontera.nombre})
                      </span>
                    )}
                  </span>
                ) : declaration.pais_transporte_frontera ? (
                  <span
                    className="truncate"
                    title={declaration.pais_transporte_frontera}
                  >
                    {declaration.pais_transporte_frontera}
                  </span>
                ) : (
                  <span className="truncate">
                    {!isSpanish
                      ? "Select Border Transport Country"
                      : "Seleccionar País Transporte Frontera"}
                  </span>
                )}
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              {isPaisTransporteFronteraModalOpen && (
                <UniversalSelectModal
                  items={paises}
                  isOpen={true}
                  onClose={() => setPaisTransporteFronteraModalOpen(false)}
                  onSelect={handlePaisTransporteFronteraSelect}
                  selectedItem={selectedPaisTransporteFrontera}
                  title={!isSpanish ? "Select Border Transport Country" : "Seleccionar País Transporte Frontera"}
                  isSpanish={isSpanish}
                  {...modalConfigNameOnly}
                />
              )}
            </div>
          </div>
        </div>
          </>
        )}

      </div>
    </>
  );
};

// Memoize component to prevent re-renders when props haven't changed Custom comparison: only re-render if declaration data for THIS specific declarationIndex changes
export const DeclarationInformation = memo(DeclarationInformationComponent, (prevProps, nextProps) => {
  // Only re-render if relevant props changed
  if (prevProps.declarationIndex !== nextProps.declarationIndex) return false;
  if (prevProps.isSpanish !== nextProps.isSpanish) return false;
  if (prevProps.handleChange !== nextProps.handleChange) return false;
  if (prevProps.setEditableData !== nextProps.setEditableData) return false;
  if (prevProps.setIsDirty !== nextProps.setIsDirty) return false;
  
  // CRITICAL: Don't check editableData array - it changes on every keystroke Only check the specific declaration we're rendering
  const prevDeclaration = prevProps.declaration;
  const nextDeclaration = nextProps.declaration;
  
  // If reference is the same, no re-render needed
  if (prevDeclaration === nextDeclaration) return true;
  
  // If reference changed, check if the specific field values we care about changed Only check top-level fields that affect rendering, not nested objects
  const fieldsToCheck = [
    'expediente', 'tipo', 'fecha_registro', 'fecha_creacion', 'fecha_operacion', 'mrn',
    'cliente_id', 'remitente_id', 'agente_id', 'clave_declarante',
    'aduana_entrada', 'aduana_salida', 'aduana_despacho',
    'pais_destino', 'pais_exportacion', 'modo_transporte_frontera',
    'factura', 'fecha_factura', 'tiene_contenedores', 'total_bultos',
    'e_mail', 'contacto_telefono', 'observaciones'
  ];
  
  // Check if any of the important fields changed
  for (const field of fieldsToCheck) {
    if (prevDeclaration?.[field] !== nextDeclaration?.[field]) {
      return false; // Field changed, re-render
    }
  }
  
  // No important fields changed, skip re-render
  return true;
});
